import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useSPAuth } from "./SPAuthContext";

export default function SPLogin() {
  const { actor, login, formatError } = useSPAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("sales@workmesh.com");
  const [password, setPassword] = useState("Sales@123");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (actor) return <Navigate to="/sales-portal/leads" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await login(email.trim(), password);
      navigate("/sales-portal/leads");
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0b0d10] text-zinc-100 flex" data-testid="sp-login-root">
      {/* Left brand panel */}
      <aside className="hidden md:flex md:w-2/5 lg:w-1/2 relative overflow-hidden border-r border-zinc-900">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(56,189,248,0.15),transparent_55%),radial-gradient(circle_at_80%_90%,rgba(217,70,239,0.10),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
        <div className="relative z-10 p-14 flex flex-col justify-between">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-sky-500/20 border border-sky-500/40 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-sky-400" />
            </div>
            <div className="text-xs uppercase tracking-[0.4em] text-zinc-400">Sales Portal</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.35em] text-sky-400/80 mb-3">Enterprise Leads Console</div>
            <h1 className="text-4xl lg:text-5xl font-light leading-tight text-zinc-50">
              Every enterprise conversation<br />
              in one focused workspace.
            </h1>
            <p className="mt-6 max-w-md text-sm text-zinc-400 leading-relaxed">
              Track inbound demo requests, qualify buyers and hand-off to closers — completely isolated from the WorkMesh product.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-zinc-600">
            Isolated · JWT · Auditable
          </div>
        </div>
      </aside>

      {/* Right form panel */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="md:hidden mb-10 flex items-center gap-3">
            <div className="h-8 w-8 rounded-sm bg-sky-500/20 border border-sky-500/40 flex items-center justify-center">
              <div className="h-3 w-3 rounded-full bg-sky-400" />
            </div>
            <div className="text-xs uppercase tracking-[0.4em] text-zinc-400">Sales Portal</div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">Sign in</div>
          <h2 className="text-2xl font-light text-zinc-100 mb-8">Access the leads console.</h2>

          <form onSubmit={submit} className="space-y-5" data-testid="sp-login-form">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2">Work email</label>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-transparent border border-zinc-800 focus:border-sky-500 focus:outline-none px-4 py-3 rounded-sm text-sm text-zinc-100 placeholder-zinc-600 transition-colors"
                placeholder="sales@workmesh.com"
                data-testid="sp-login-email"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.35em] text-zinc-500 mb-2">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-transparent border border-zinc-800 focus:border-sky-500 focus:outline-none px-4 py-3 rounded-sm text-sm text-zinc-100 placeholder-zinc-600 transition-colors"
                placeholder="••••••••"
                data-testid="sp-login-password"
              />
            </div>

            {err ? (
              <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/30 px-3 py-2 rounded-sm" data-testid="sp-login-error">
                {err}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 bg-sky-500 hover:bg-sky-400 disabled:opacity-60 text-zinc-950 text-sm uppercase tracking-[0.25em] rounded-sm transition-colors"
              data-testid="sp-login-submit"
            >
              {busy ? "Signing in…" : "Enter portal"}
            </button>
          </form>

          <div className="mt-10 text-[11px] text-zinc-600 leading-relaxed border-t border-zinc-900 pt-6">
            <div className="uppercase tracking-[0.3em] text-zinc-500 mb-2">Demo credentials</div>
            <div>Sales user &nbsp;·&nbsp; <span className="text-zinc-400">sales@workmesh.com</span> &nbsp;/&nbsp; <span className="text-zinc-400">Sales@123</span></div>
            <div>WorkMesh admin also works with existing credentials.</div>
          </div>
        </div>
      </main>
    </div>
  );
}
